import { FormikProps } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { PrimaryButton } from '../../../../../shared/Button';
import { FormFieldRow } from '../../../../../shared/form/FormFieldRow';
import { getNullSelectOption, Select, SelectOptions } from '../../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Header3 } from '../../../../../shared/Headers';
import { Modal, ModalActionButtons, ModalHeader } from '../../../../../shared/Modal';
import { colourRed06 } from '../../../../../styling/colours';
import { spacing04 } from '../../../../../styling/spacing';
import { DesignFormModel } from '../../../../designs/design-form/designFormModel';
import {
  DesignFormDefaultsContext,
  getInitialUnitFloorHeatLossDetailsFormModel,
  getInitialUnitRoofHeatLossDetailsFormModel,
  getInitialUnitWallHeatLossDetailsFormModel,
  UnitFloorHeatLossFieldValue,
  UnitHeatLossFieldValue,
} from '../../../../designs/DesignFormDefaultsContext';
import { BuildTypeCode, buildTypeSelectOptions } from '../../../../projects/project';
import { ManifoldFormModel } from '../../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { defaultRoomAirChangeRatesByRoomType } from '../../rooms/heatLoss/roomHeatLossDefaults';
import { UnitFieldContext } from '../UnitFieldContext';
import { RoomsHeatLossTable } from './RoomsHeatLossTable';
import { RoomsHeatLossTableViewType, RoomsHeatLossTableViewTypeButtons } from './RoomsHeatLossTableViewTypeButtons';
import {
  onUnitFloorTemperatureChange,
  onUnitRoofTemperatureChange,
  onUnitWallTemperatureChange,
} from './unitElementOnChangeHelpers';
import { UnitFloorElementsField } from './UnitFloorElementsField';
import { UnitRoofElementsField } from './UnitRoofElementsField';
import { UnitWallElementsField } from './UnitWallElementsField';

type Props = {
  roomsAreInvalid: boolean;
  isOpen: boolean;
  closeModal: () => void;
};

export const UnitHeatLossDetailsModal = ({ roomsAreInvalid, isOpen, closeModal }: Props) => {
  const [filteredManifoldId, setFilteredManifoldId] = useState<string | null>(null);
  const [roomsHeatLossTableViewType, setRoomsHeatLossTableViewType] = useState<RoomsHeatLossTableViewType>('Roof');

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { externalAirTemperatureInDegreesCelsius: projectExternalAirTemperature } = formContext;

  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));
  const { unit, unitIndex } = unitFieldContext;

  const [unitRoofElements, setUnitRoofElements] = useState<Array<UnitHeatLossFieldValue>>([]);
  const [unitWallElements, setUnitWallElements] = useState<Array<UnitHeatLossFieldValue>>([]);
  const [unitFloorElements, setUnitFloorElements] = useState<Array<UnitFloorHeatLossFieldValue>>([]);

  const [externalAirTemperatureInDegreesCelsius, setExternalAirTemperatureInDegreesCelsius] = useState<string>(
    unit.heatLossDetails?.externalAirTemperatureInDegreesCelsius ?? projectExternalAirTemperature.toString(),
  );

  const unitHeatLossDetailsFieldName = (fieldName: string) => `units.${unitIndex}.heatLossDetails.${fieldName}`;

  useEffect(() => {
    if (isOpen) {
      setUnitRoofElements(getInitialUnitRoofHeatLossDetailsFormModel(unit));
      setUnitWallElements(getInitialUnitWallHeatLossDetailsFormModel(unit));
      setUnitFloorElements(getInitialUnitFloorHeatLossDetailsFormModel(unit));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const manifoldFilterOptions = useMemo(
    () => [
      getNullSelectOption<string>(),
      ...unit.manifolds.map(manifold => ({
        value: manifold.id,
        text: manifold.name,
      })),
    ],
    [unit],
  );

  const updateAirChangesDetails = (newValue: BuildTypeCode) => {
    unit.manifolds.forEach((manifold, manifoldIndex) => {
      manifold.rooms?.forEach((room, roomIndex) => {
        if (room.heatLossDetails != null && room.roomType != null) {
          const airChangesDetails = defaultRoomAirChangeRatesByRoomType[room.roomType];

          if (
            room.heatLossDetails.numberOfAirChangesPerHour ===
            airChangesDetails[unit.heatLossDetails?.buildTypeCode ?? 'NewBuild'].NumberOfAirChangesPerHour.toString()
          ) {
            formContext.formProps.setFieldValue(
              `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.numberOfAirChangesPerHour`,
              airChangesDetails[newValue].NumberOfAirChangesPerHour.toString(),
            );
          }

          if (
            room.heatLossDetails.mechanicalVentilationHeatRecoveryEfficiencyPercentage ===
            airChangesDetails[unit.heatLossDetails?.buildTypeCode ?? 'NewBuild'].mvhrEfficiencyInPercentage.toString()
          ) {
            formContext.formProps.setFieldValue(
              `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.mechanicalVentilationHeatRecoveryEfficiencyPercentage`,
              airChangesDetails[newValue].mvhrEfficiencyInPercentage.toString(),
            );
          }
        }
      });
    });
  };

  const updateExternalAirTemperature = (newValue: string) => {
    const newManifoldValues: Array<ManifoldFormModel> = [];
    unitWallElements.forEach((wallElement, wallIndex) => {
      if (wallElement.value === externalAirTemperatureInDegreesCelsius) {
        onUnitWallTemperatureChange(
          newValue,
          unitIndex,
          wallIndex,
          formContext.formProps as FormikProps<DesignFormModel>,
          unitWallElements,
          setUnitWallElements,
          newManifoldValues,
          false,
        );
        formContext.formProps.setFieldValue(
          `units.${unitIndex}.heatLossDetails.wallElements.${wallIndex}.defaultExternalAirTemperatureInDegreesCelsius`,
          newValue,
        );
      }
    });

    unitRoofElements.forEach((roofElement, roofIndex) => {
      if (roofElement.value === externalAirTemperatureInDegreesCelsius) {
        onUnitRoofTemperatureChange(
          newValue,
          unitIndex,
          roofIndex,
          formContext.formProps as FormikProps<DesignFormModel>,
          unitRoofElements,
          setUnitRoofElements,
          newManifoldValues,
          false,
        );
        formContext.formProps.setFieldValue(
          `units.${unitIndex}.heatLossDetails.roofElements.${roofIndex}.defaultTemperatureAboveInDegreesCelsius`,
          newValue,
        );
      }
    });

    unitFloorElements.forEach((floorElement, floorIndex) => {
      if (floorElement.temperature === externalAirTemperatureInDegreesCelsius) {
        onUnitFloorTemperatureChange(
          newValue,
          unitIndex,
          floorIndex,
          formContext.formProps as FormikProps<DesignFormModel>,
          unitFloorElements,
          setUnitFloorElements,
          null,
          newManifoldValues,
          false,
        );
        formContext.formProps.setFieldValue(
          `units.${unitIndex}.heatLossDetails.floorElements.${floorIndex}.defaultTemperatureBelowInDegreesCelsius`,
          newValue,
        );
      }
    });

    formContext.formProps.setFieldValue(`units.${unitIndex}.manifolds`, newManifoldValues);
    setExternalAirTemperatureInDegreesCelsius(newValue);
  };

  return (
    <Modal isOpen={isOpen} isWide={true}>
      <DesignFormDefaultsContext.Provider
        value={{
          unitRoofElements,
          setUnitRoofElements,
          unitFloorElements,
          setUnitFloorElements,
          unitWallElements,
          setUnitWallElements,
        }}
      >
        <ModalHeader>Edit Heat Loss Details</ModalHeader>
        <FormFieldRow>
          <TextInputField
            name={unitHeatLossDetailsFieldName('generalAllowanceFactorPercentage')}
            label="General Allowance Factor (%)"
          />
          <SelectField
            name={unitHeatLossDetailsFieldName('exposureFactorPercentage')}
            label="Exposure Factor"
            options={exposureFactorSelectOptions}
          />
          <SelectField
            name={unitHeatLossDetailsFieldName('buildTypeCode')}
            label="Build Type"
            options={buildTypeSelectOptions}
            onChange={updateAirChangesDetails}
          />
          <TextInputField
            name={unitHeatLossDetailsFieldName('externalAirTemperatureInDegreesCelsius')}
            label="External Air Temperature (°C)"
            onChange={updateExternalAirTemperature}
          />
          <TextInputField
            name={unitHeatLossDetailsFieldName('propertyFootprintInMetresSquared')}
            label={
              <>
                Property Footprint (m<sup>2</sup>)
              </>
            }
          />
          <TextInputField
            name={unitHeatLossDetailsFieldName('propertyExternalPerimeterInMetres')}
            label="Property External Perimeter (m)"
          />
        </FormFieldRow>

        <UnitWallElementsField
          externalAirTemperatureInDegreesCelsiusString={externalAirTemperatureInDegreesCelsius}
          unit={unit}
          unitIndex={unitIndex}
        />
        <UnitRoofElementsField unit={unit} unitIndex={unitIndex} />
        <UnitFloorElementsField unit={unit} unitIndex={unitIndex} />

        <RoomsHeader $hasNestedErrors={roomsAreInvalid}>Rooms</RoomsHeader>
        <RoomsTableViewControlsContainer>
          <RoomsManifoldFilterSelectContainer>
            <RoomsManifoldFilterSelectLabel>Filter by Manifold</RoomsManifoldFilterSelectLabel>
            <Select<string | null>
              onChange={newValue => setFilteredManifoldId(newValue)}
              value={filteredManifoldId}
              options={manifoldFilterOptions}
            />
          </RoomsManifoldFilterSelectContainer>
          <RoomsTableViewTypeButtonsContainer>
            <RoomsHeatLossTableViewTypeButtons
              selectedViewType={roomsHeatLossTableViewType}
              setViewType={setRoomsHeatLossTableViewType}
              unitIndex={unitIndex}
            />
          </RoomsTableViewTypeButtonsContainer>
        </RoomsTableViewControlsContainer>
        <RoomsHeatLossTable
          unit={unit}
          unitIndex={unitIndex}
          filteredManifoldId={filteredManifoldId}
          selectedViewType={roomsHeatLossTableViewType}
        />

        <ModalActionButtons $align="right">
          <PrimaryButton onClick={closeModal} data-testid={closeHeatLossDetailsModalButtonTestId}>
            Save
          </PrimaryButton>
        </ModalActionButtons>
      </DesignFormDefaultsContext.Provider>
    </Modal>
  );
};

export const closeHeatLossDetailsModalButtonTestId = 'close-heat-loss-modal-button';

const RoomsHeader = styled(Header3)<{ $hasNestedErrors: boolean }>`
  color: ${props => (props.$hasNestedErrors ? colourRed06 : undefined)};
`;

const RoomsTableViewControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: ${spacing04};
`;

const RoomsManifoldFilterSelectContainer = styled.div`
  width: 15%;
`;

const RoomsManifoldFilterSelectLabel = styled.div`
  padding-bottom: ${spacing04};
  font-weight: bold;
`;

const RoomsTableViewTypeButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const exposureFactorSelectOptions: SelectOptions<0 | 10> = [
  { text: '0%', value: 0 },
  { text: '10%', value: 10 },
];
